// ============================================================================
// Formatage FR (dates, statuts, libellés) partagé par les pages « demande ».
// Source de vérité des statuts/actions : server/utils/statuts.ts (miroir dans
// client/types/index.ts). Ici on ne fait que de l'affichage.
// ============================================================================
import type { Action, CanalRecherche, EtatPrevenu, PersonnePrevenue, Statut, StatutFak, TypeDemande } from '../../types';

// Rappel Tipee — l'absence doit toujours y être enregistrée (DECISIONS.md §5),
// même quand l'atelier est fusionné/annulé ou qu'aucun remplaçant n'est requis.
export const BANNIERE_TIPEE = "N'oubliez pas de faire votre demande de congé dans Tipee.";

// ── Dates ────────────────────────────────────────────────────────────────
/**
 * Une date civile « AAAA-MM-JJ » (le jour d'un atelier) est un jour du CALENDRIER : lue en minuit local, jamais en minuit UTC — sinon elle recule d'un
 * jour à l'ouest de Greenwich. Une date-heure complète (horodatage) reste un instant.
 */
function enDate(date: string | Date): Date {
  if (typeof date !== 'string') return date;
  const civile = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  return civile ? new Date(Number(civile[1]), Number(civile[2]) - 1, Number(civile[3])) : new Date(date);
}

// 'lun. 6 juil.' — pour les tableaux et listes compactes.
export function formatDateCourt(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = enDate(date);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-CH', { weekday: 'short', day: 'numeric', month: 'short' }).format(d);
}

// 'lundi 6 juillet 2026' — pour les en-têtes de fiche.
export function formatDateLongue(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = enDate(date);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(d).replace(',', '');
}

// '2 juil. 2026 à 11:06' — horodatages (événements, réponses…).
export function formatDateHeure(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-CH', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
}

// 'il y a 2 h' / 'hier' / 'lun. 6 juil.' — pour la chronologie (Historique).
export function formatRelatif(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'à l’instant';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `il y a ${diffH} h`;
  const diffJ = Math.round(diffH / 24);
  if (diffJ === 1) return 'hier';
  if (diffJ < 7) return `il y a ${diffJ} j`;
  return formatDateCourt(d);
}

// ── Échéances ────────────────────────────────────────────────────────────
// Classe CSS (public/main.css) selon l'urgence d'une échéance à venir.
export function classeEcheance(date: string | Date | null | undefined, seuilHeuresProche = 48): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  const diffH = (d.getTime() - Date.now()) / 3_600_000;
  if (diffH < 0) return 'echeance-ratee';
  if (diffH <= seuilHeuresProche) return 'echeance-proche';
  return 'echeance-ok';
}

// ── Statuts : couche « méta-états » (SOURCE UNIQUE) ───────────────────────
// Les 21 statuts précis de la machine à états (server/utils/statuts.ts) sont
// regroupés en quelques méta-états en langage simple : c'est ce que lit d'abord
// l'utilisateur·rice (badge coloré + gros), le statut précis restant affiché
// en petit dessous. Aucune modification de la machine à états — pur affichage.
export type MetaEtat = 'a_traiter' | 'recherche' | 'urgence' | 'finalisation' | 'passation' | 'termine' | 'sans_suite';

/** Ordre d'affichage (filtres, légende). */
export const META_ETATS_ORDRE: MetaEtat[] = ['a_traiter', 'recherche', 'urgence', 'finalisation', 'passation', 'termine', 'sans_suite'];

/** Libellé lisible du méta-état (le gros badge). */
export const META_ETAT_LABEL: Record<MetaEtat, string> = {
  a_traiter: 'À traiter',
  recherche: 'Recherche en cours',
  urgence: 'Urgence',
  finalisation: 'Remplaçant·e trouvé·e',
  passation: 'Remplacement organisé',
  termine: 'Terminé',
  sans_suite: 'Sans suite',
};

/** Couleur (design-kit) du méta-état : vert=ok, orange=en cours, rouge=urgent, bleu=en pipeline, gris=abandonné. */
export const META_ETAT_BADGE: Record<MetaEtat, string> = {
  a_traiter: 'badge badge-accent',
  recherche: 'badge badge-warn',
  urgence: 'badge badge-err',
  finalisation: 'badge badge-accent',
  passation: 'badge badge-accent',
  termine: 'badge badge-ok',
  sans_suite: 'badge badge-plain',
};

/** Statut précis → méta-état. Doit couvrir TOUS les statuts (Record exhaustif). */
export const STATUT_META: Record<Statut, MetaEtat> = {
  brouillon: 'a_traiter',
  soumise: 'a_traiter',
  recherche_interne_1: 'recherche',
  recherche_interne_2: 'recherche',
  recherche_externe: 'recherche',
  fusion_demandee: 'recherche',
  evaluation_annulation: 'recherche',
  urgence_cascade: 'urgence',
  validation_responsable: 'finalisation',
  validation_rh: 'finalisation',
  attente_tipee: 'finalisation',
  passation: 'passation',
  atelier_donne: 'passation',
  absence_validee: 'termine',
  cloturee: 'termine',
  fusionnee: 'termine',
  maintenue: 'termine',
  annulee: 'sans_suite',
  refusee: 'sans_suite',
  sans_suite: 'sans_suite',
};

/** Repli FR quand le libellé précis (api.getMeta(), asynchrone) n'est pas
 *  encore arrivé ou a échoué — sans ça, StatutBadge retombe sur la valeur
 *  brute de la machine à états (`recherche_interne_1`…), du jargon technique
 *  incompréhensible pour l'audience visée. Miroir exact de
 *  server/utils/statuts.ts::LIBELLES_STATUTS (source de vérité serveur) —
 *  à garder synchronisé si un statut est ajouté/renommé côté serveur. */
export const LIBELLES_STATUT_REPLI: Record<Statut, string> = {
  brouillon: 'Brouillon',
  soumise: 'Soumise',
  recherche_interne_1: 'Recherche interne — 1er appel',
  recherche_interne_2: 'Recherche interne — relance',
  recherche_externe: 'Recherche externe (vivier)',
  fusion_demandee: "Fusion d'atelier en cours",
  evaluation_annulation: 'Évaluation : annuler ou maintenir ?',
  validation_responsable: 'À valider par le/la responsable',
  validation_rh: 'À valider par les RH',
  attente_tipee: 'En attente de la validation RH dans Tipee',
  passation: 'Passation en cours',
  atelier_donne: 'Atelier assuré — présences à contrôler',
  urgence_cascade: 'Urgence — recherche en cascade',
  absence_validee: 'Absence validée (sans remplacement)',
  cloturee: 'Clôturée',
  fusionnee: 'Atelier fusionné',
  annulee: 'Atelier annulé',
  maintenue: 'Atelier maintenu',
  refusee: 'Refusée',
  sans_suite: 'Classée sans suite',
};

/** Inverse : méta-état → liste des statuts précis (pour filtrer par méta-état). */
export const META_ETAT_STATUTS: Record<MetaEtat, Statut[]> = (Object.entries(STATUT_META) as [Statut, MetaEtat][])
  .reduce((acc, [statut, meta]) => {
    (acc[meta] ??= []).push(statut);
    return acc;
  }, {} as Record<MetaEtat, Statut[]>);

/** Méta-état correspondant EXACTEMENT à un ensemble de statuts (pour refléter le filtre actif), sinon ''. */
export function metaEtatDeStatuts(statuts: Statut[]): MetaEtat | '' {
  if (statuts.length === 0) return '';
  const trouve = META_ETATS_ORDRE.find((m) => {
    const groupe = META_ETAT_STATUTS[m];
    return groupe.length === statuts.length && groupe.every((s) => statuts.includes(s));
  });
  return trouve ?? '';
}

/** Classe(s) de badge (design-kit) pour un statut — dérivée du méta-état (source unique). */
export function classeBadgeStatut(statut: Statut): string {
  return META_ETAT_BADGE[STATUT_META[statut]];
}

// ── Types de demande ────────────────────────────────────────────────────
export const LIBELLES_TYPE: Record<TypeDemande, string> = {
  planifie: 'Planifié',
  urgence_weekend: 'Urgence week-end',
  urgence_semaine: 'Urgence semaine',
};

// ── Actions (boutons du panneau d'actions) ──────────────────────────────
export const LIBELLES_ACTION: Record<Action, string> = {
  soumettre: 'Soumettre',
  ecarter: 'Écarter (sans objet)',
  valider_absence_simple: "Valider l'absence (sans remplaçant)",
  saisir_remplacant: 'Désigner un·e remplaçant·e',
  confirmer_remplacement: 'Je confirme être remplaçant·e',
  refuser_remplacement: 'Décliner',
  retirer_remplacant: 'Choisir quelqu’un d’autre',
  noter_seance_assuree: 'A assuré la séance',
  lancer_recherche: 'Lancer la recherche Slack',
  relancer_slack: 'Relancer sur Slack',
  passer_externe: 'Passer à la recherche externe',
  annuler_demande: 'Annuler la demande',
  demander_fusion: 'Demander une fusion',
  confirmer_fusion: 'Confirmer la fusion',
  fusion_impossible: 'Fusion impossible',
  annuler_atelier: "Annuler l'atelier",
  refuser: 'Refuser',
  valider_responsable: 'Valider (responsable)',
  valider_rh: 'Valider (RH)',
  renvoyer_demande: 'Renvoyer (incomplet)',
  confirmer_tipee: 'Absence validée dans Tipee → passation',
  refuser_absence_rh: 'Absence refusée par les RH',
  terminer_passation: 'Passation transmise',
  cloturer: 'Clôturer',
  declencher_urgence: 'Déclencher une urgence',
  urgence_fusion: 'Fusion (urgence)',
  urgence_maintien: 'Maintien (présence contenante)',
  urgence_annulation: 'Annulation (urgence)',
};

/** Actions destructives / de refus → bouton .btn-danger. */
export const ACTIONS_DANGER: Action[] = [
  'ecarter', 'annuler_demande', 'annuler_atelier', 'refuser', 'fusion_impossible', 'urgence_annulation', 'refuser_absence_rh',
];
/** Actions neutres (retour en arrière) → bouton .btn-ghost. */
export const ACTIONS_NEUTRES: Action[] = ['renvoyer_demande', 'refuser_remplacement', 'retirer_remplacant'];

// Conséquence en clair pour la pop-up de confirmation des actions qui
// s'exécutent en un clic (pas de formulaire propre) — voir DemandePage.tsx.
// Reflète server/utils/statuts.ts::TRANSITIONS (statut de destination).
export const DESCRIPTIONS_ACTION: Partial<Record<Action, string>> = {
  soumettre: 'La demande sera soumise et entrera dans le circuit de traitement.',
  ecarter: 'La demande sera classée sans suite (sans objet). Action définitive.',
  valider_absence_simple: "L'absence sera validée, sans recherche de remplaçant·e.",
  lancer_recherche: "Un 1er message pour Slack sera préparé pour rechercher un·e remplaçant·e. S'il ne part pas tout seul, il restera à le publier à la main depuis la fiche.",
  relancer_slack: "Un message de relance pour Slack sera préparé. S'il ne part pas tout seul, il restera à le publier à la main depuis la fiche.",
  passer_externe: 'La recherche s’élargira au vivier externe.',
  fusion_impossible: 'La fusion est écartée ; la demande repart en évaluation (annulation ou maintien).',
  valider_responsable: 'Validation du/de la responsable — la demande passera ensuite en validation RH.',
  valider_rh: 'Validation RH — la demande passera ensuite à l’étape Tipee.',
  renvoyer_demande: 'La demande repart en recherche (informations incomplètes).',
  confirmer_tipee: "À utiliser seulement si la synchro n'a pas reconnu l'absence : vous confirmez qu'elle est validée dans Tipee, la passation s'ouvre.",
  terminer_passation: 'La passation est transmise au/à la remplaçant·e, qui en est prévenu·e dès qu’il/elle a confirmé — prochaine étape : l’atelier.',
};

// ── Canaux de recherche ──────────────────────────────────────────────────
export const LIBELLES_CANAL: Record<CanalRecherche, string> = {
  slack_1: '1er message Slack',
  slack_2: 'Relance Slack',
  email_externe: 'E-mails externes',
  relance_tel: 'Relance téléphonique',
  cascade_urgence: 'Cascade urgence',
};

// ── Événements (fil Historique) ──────────────────────────────────────────
export const LIBELLES_EVENEMENT: Record<string, string> = {
  detection_auto: 'Demande créée automatiquement : absence repérée dans Tipee',
  demande_creee: 'Demande créée',
  date_modifiee: 'Date modifiée',
  notes_modifiees: 'Notes modifiées',
  flexibilite_modifiee: 'Flexibilité des dates modifiée',
  cascade_maj: "Étape de l'urgence mise à jour",
  demande_soumise: 'Demande soumise',
  demande_ecartee: 'Demande écartée (sans objet)',
  demande_supprimee: 'Demande supprimée',
  demande_restauree: 'Demande restaurée',
  absence_validee: 'Absence validée (sans remplaçant)',
  remplacant_saisi: 'Remplaçant·e saisi·e',
  remplacant_confirme: 'Remplaçant·e a confirmé',
  remplacant_refuse: 'Remplaçant·e a décliné ou s’est désisté·e',
  remplacant_retire: 'Remplaçant·e retiré·e : quelqu’un d’autre sera choisi',
  seance_assuree_notee: 'Séance assurée : notée par l’équipe gestion',
  confirmation_en_retard: 'Pas de réponse du/de la remplaçant·e à l’échéance : alerte envoyée',
  confirmation_relancee: 'Remplaçant·e relancé·e',
  dev_delai_confirmation: 'Test : délai de réponse écoulé',
  recherche_slack_1: '1er message Slack préparé',
  recherche_slack_2: 'Relance Slack préparée',
  message_envoye: 'Message marqué comme envoyé',
  recherche_externe_lancee: 'Recherche externe lancée',
  demande_annulee: 'Demande annulée',
  fusion_demandee: 'Fusion demandée',
  atelier_fusionne: 'Atelier fusionné',
  fusion_impossible: 'Fusion jugée impossible',
  atelier_annule: 'Atelier annulé',
  demande_refusee: 'Demande refusée',
  validation_responsable_ok: 'Validé par le/la responsable',
  validation_rh_ok: 'Validé par les RH',
  demande_renvoyee: 'Renvoyée (incomplète)',
  tipee_valide: 'Absence validée dans Tipee — passation ouverte',
  tipee_absence_validee: 'Absence validée par les RH, constatée dans Tipee',
  tipee_a_completer: 'Demande Tipee à compléter (RH)',
  absence_refusee_rh: 'Absence refusée par les RH',
  hors_solde_confirme: 'Vacances hors solde : envoyée malgré le solde insuffisant',
  passation_terminee: 'Passation terminée',
  demande_cloturee: 'Demande clôturée',
  urgence_declenchee: 'Urgence déclenchée',
  urgence_atelier_fusionne: 'Urgence : atelier fusionné',
  urgence_atelier_maintenu: 'Urgence : atelier maintenu',
  urgence_atelier_annule: 'Urgence : atelier annulé',
  prevenus_personnellement: 'Collègues prévenu·e·s personnellement',
};

export function libelleEvenement(eventType: string, peutGerer = true): string {
  // La recherche élargie est l'affaire de la gestion : l'intervenant·e n'en voit
  // que le fait, sans parler de vivier ni d'externes.
  if (!peutGerer && eventType === 'recherche_externe_lancee') return "L'équipe gestion a élargi la recherche";
  return LIBELLES_EVENEMENT[eventType] ?? eventType.replace(/_/g, ' ');
}

// ── « Prévenir aussi personnellement » (DECISIONS §26) ───────────────────

/** La phrase d'un message personnel : « Alexia Sestito pense à vous pour ce remplacement » (de = la personne absente, ou « L'équipe gestion »). */
export function phrasePenseAVous(de: string): string {
  return `${de} pense à vous pour ce remplacement`;
}

/**
 * Ce que la fiche dit de chaque personne prévenue personnellement (gestion, personne absente, auteur·rice seulement).
 * « non_prevenu » : aucun message n'a été créé (la personne l'a désactivé dans ses préférences, ou c'est elle qui a lancé la recherche).
 */
export const LIBELLES_ETAT_PREVENU: Record<EtatPrevenu, string> = {
  envoye: 'message envoyé',
  sans_compte: 'pas de compte : pas de message',
  en_attente: 'le message partira avec la recherche',
  non_prevenu: 'pas de message',
};

/**
 * « David Viale (message envoyé), Myriam Masullo (pas de compte : pas de message) ».
 * `rechercheLancee` : « en attente » veut dire « le message n'est pas parti » (la recherche a démarré sans lui) plutôt que « partira avec la recherche ».
 */
export function listePrevenus(prevenus: PersonnePrevenue[], rechercheLancee = false): string {
  return prevenus.map((p) => `${p.nom} (${p.etat === 'en_attente' && rechercheLancee ? "le message n'est pas parti" : LIBELLES_ETAT_PREVENU[p.etat] ?? p.etat})`).join(', ');
}

/**
 * L'info-bulle du badge « Dates flexibles » : la personne absente peut décaler ses dates si personne n'est trouvé·e.
 * Pour elle-même : « vous » ; pour la gestion et l'auteur·rice : son nom.
 */
export function infobulleDatesFlexibles(absentNom: string | null | undefined, estLaPersonneAbsente: boolean): string {
  return estLaPersonneAbsente
    ? "Vous pouvez décaler vos dates d'absence si aucun·e remplaçant·e n'est trouvé·e. L'équipe gestion le voit sur la demande."
    : `${absentNom || 'La personne absente'} peut décaler ses dates d'absence si aucun·e remplaçant·e n'est trouvé·e.`;
}

// ── FRA ──────────────────────────────────────────────────────────────────
export const LIBELLES_STATUT_FAK: Record<StatutFak, string> = {
  manquant: 'Manquant',
  brouillon: 'Brouillon',
  publie: 'Publié',
  a_reviser: 'À réviser',
  archive: 'Archivé',
};

export function classeBadgeFak(statut: StatutFak): string {
  if (statut === 'publie') return 'badge badge-ok';
  if (statut === 'a_reviser') return 'badge badge-warn';
  if (statut === 'archive') return 'badge badge-plain';
  return 'badge badge-accent';
}

/** Adresse web Tipee (réglage tipee.instance_url), complétée en https:// si saisie sans protocole. */
export function lienTipee(v?: string | null): string {
  const u = (v ?? '').trim();
  return u && !/^https?:\/\//i.test(u) ? `https://${u}` : u;
}
